import { DROPBOX_FOLDER_STATUS } from './dropbox.constants';
import type { DropboxFolderStatus } from './dropbox.types';

export const normalizeFolderSegment = (value: string) => {
  let normalized = value.trim();
  while (normalized.startsWith('/')) {
    normalized = normalized.slice(1);
  }
  while (normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
};

export const buildDropboxPath = (...segments: Array<string | null | undefined>) => {
  const cleaned = segments
    .map(segment => normalizeFolderSegment(segment || ''))
    .filter(Boolean);
  return `/${cleaned.join('/')}`;
};

export const isAbsoluteDropboxPath = (value: string | null | undefined) => Boolean(value?.trim().startsWith('/'));

export const resolveDropboxPath = (value: string | null | undefined, dropboxRoot?: string) => {
  const trimmed = value?.trim() || '';
  if (!trimmed) {
    return '';
  }
  if (isAbsoluteDropboxPath(trimmed) || !dropboxRoot) {
    return buildDropboxPath(trimmed);
  }
  return buildDropboxPath(dropboxRoot, trimmed);
};

export const getDropboxRelativePath = (childPath: string, parentPath: string) => {
  const normalizedParent = buildDropboxPath(parentPath);
  const normalizedChild = buildDropboxPath(childPath);
  if (normalizedChild === normalizedParent) {
    return '';
  }
  const parentPrefix = `${normalizedParent}/`;
  if (normalizedChild.startsWith(parentPrefix)) {
    return normalizedChild.slice(parentPrefix.length);
  }
  return normalizeFolderSegment(normalizedChild);
};

export const getDropboxFolderDisplayName = (path: string) => {
  const segments = buildDropboxPath(path).split('/').filter(Boolean);
  return segments.at(-1) ?? path;
};

export type DropboxFolderMatchKind = 'exact' | 'partial';


export const stripDropboxRootPrefix = (path: string | null | undefined, dropboxRoots: string[] = []) => {
  const absolute = buildDropboxPath(path || '');
  if (!absolute || absolute === '/') {
    return '';
  }
  const normalizedRoots = dropboxRoots
    .map(root => buildDropboxPath(root))
    .sort((a, b) => b.length - a.length);

  const matchedRoot = normalizedRoots.find(root => absolute === root || absolute.startsWith(`${root}/`));
  if (matchedRoot) {
    if (absolute === matchedRoot) {
      return '';
    }
    return absolute.slice(matchedRoot.length + 1);
  }

  return normalizeFolderSegment(absolute);
};

export const getDropboxFolderMatchKind = (
  option: { name: string; pathDisplay: string },
  query: string,
  scopePath: string,
  options?: { relativeToParent?: boolean }
): DropboxFolderMatchKind | null => {
  const queryLower = normalizeFolderSegment(query).toLowerCase();
  if (!queryLower) {
    return null;
  }

  const nameLower = normalizeFolderSegment(option.name).toLowerCase();
  const relativePathLower = options?.relativeToParent
    ? getDropboxRelativePath(option.pathDisplay, scopePath).toLowerCase()
    : stripDropboxRootPrefix(option.pathDisplay, [scopePath]).toLowerCase();

  if (nameLower === queryLower || relativePathLower === queryLower) {
    return 'exact';
  }
  if (nameLower.includes(queryLower) || relativePathLower.includes(queryLower)) {
    return 'partial';
  }
  return null;
};


export const getDropboxFolderStatusConfig = (status: DropboxFolderStatus) => {
  switch (status) {
    case DROPBOX_FOLDER_STATUS.SEARCHING:
      return { label: 'Searching...', color: 'default' as const };
    case DROPBOX_FOLDER_STATUS.EXISTS:
      return { label: 'Folder Exists', color: 'success' as const };
    case DROPBOX_FOLDER_STATUS.MATCHES:
      return { label: 'Similar Matches Found', color: 'info' as const };
    case DROPBOX_FOLDER_STATUS.NOT_FOUND:
      return { label: 'Not Found', color: 'error' as const };
    case DROPBOX_FOLDER_STATUS.CREATING:
      return { label: 'Creating...', color: 'default' as const };
    case DROPBOX_FOLDER_STATUS.CREATED:
      return { label: 'Created', color: 'success' as const };
    case DROPBOX_FOLDER_STATUS.SYNCING:
      return { label: 'Syncing...', color: 'default' as const };
    case DROPBOX_FOLDER_STATUS.ERROR:
      return { label: 'Error', color: 'error' as const };
    default:
      return { label: 'Not Checked', color: 'default' as const };
  }
};
